import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { StickyNote, Loader2, Plus, Save, X, Briefcase, Clipboard, User, MessageSquare } from 'lucide-react';
import { customersAPI, estimatesAPI, crmJobsAPI } from '../lib/api';
import { toast } from 'sonner';
import { formatInTenantTimezone } from '../lib/timezone';
import { useNavigate } from 'react-router-dom';

function NoteItem({ note }) {
  const navigate = useNavigate();
  return (
    <div className="py-2.5 px-3 rounded hover:bg-muted/40 transition-colors" data-testid={`note-item-${note.id || note.type}`}>
      <div className="flex items-start gap-3">
        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0 mt-0.5">
          {note.type === 'customer' && <User className="w-3.5 h-3.5 text-muted-foreground" />}
          {note.type === 'job' && <Briefcase className="w-3.5 h-3.5 text-muted-foreground" />}
          {note.type === 'estimate' && <Clipboard className="w-3.5 h-3.5 text-muted-foreground" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{note.text}</p>
          <div className="flex items-center gap-2 mt-1">
            {note.author && <span className="text-xs font-medium text-muted-foreground">{note.author}</span>}
            {note.link && (
              <button
                className="text-xs text-primary hover:underline"
                onClick={() => navigate(note.link)}
              >
                {note.link_label}
              </button>
            )}
            {note.timestamp && (
              <span className="text-xs text-muted-foreground">
                {formatInTenantTimezone(note.timestamp, { showTimezone: false, dateStyle: 'medium', timeStyle: 'short' })}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CustomerNotesTabs({ customerId, customer, onUpdate }) {
  const [tab, setTab] = useState('all');
  const [jobNotes, setJobNotes] = useState([]);
  const [estimateNotes, setEstimateNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadNotes = async () => {
      setLoading(true);
      try {
        // Load jobs with notes
        const jobsRes = await crmJobsAPI.list({ customer_id: customerId, page_size: 100 });
        const jobs = jobsRes.data?.jobs || [];
        const jNotes = jobs
          .filter(j => j.notes && j.notes.trim())
          .map(j => ({
            id: `job-${j.id}`,
            type: 'job',
            text: j.notes,
            link: `/jobs/${j.id}`,
            link_label: `Job #${j.job_number}`,
            timestamp: j.updated_at || j.created_at,
          }));
        setJobNotes(jNotes);

        // Load estimate notes
        const estRes = await estimatesAPI.list({ customer_id: customerId, page_size: 100 });
        const estimates = estRes.data?.estimates || [];
        const eNotes = [];
        for (const est of estimates) {
          try {
            const detail = await estimatesAPI.get(est.id);
            const estData = detail.data;
            if (estData.notes && estData.notes.length > 0) {
              for (const n of estData.notes) {
                eNotes.push({
                  id: `est-note-${n.id}`,
                  type: 'estimate',
                  text: n.note,
                  author: n.author_name,
                  link: `/estimates/${est.id}`,
                  link_label: est.estimate_number,
                  timestamp: n.created_at,
                });
              }
            }
          } catch { /* skip */ }
        }
        setEstimateNotes(eNotes);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    };
    loadNotes();
  }, [customerId]);

  const customerNotes = customer.customer_notes
    ? [{ id: 'customer-main', type: 'customer', text: customer.customer_notes, timestamp: customer.updated_at }]
    : [];

  const allNotes = [...customerNotes, ...jobNotes, ...estimateNotes]
    .sort((a, b) => (b.timestamp || '') > (a.timestamp || '') ? 1 : -1);

  const tabNotes = {
    all: allNotes,
    customer: customerNotes,
    estimates: estimateNotes,
    jobs: jobNotes,
  };

  const current = tabNotes[tab] || [];

  const handleSaveNote = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    try {
      const existing = customer.customer_notes || '';
      const combined = existing ? `${existing}\n\n---\n\n${newNote.trim()}` : newNote.trim();
      const res = await customersAPI.update(customerId, { customer_notes: combined });
      onUpdate(res.data);
      setNewNote('');
      setAdding(false);
      toast.success('Note added');
    } catch {
      toast.error('Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="customer-notes-tabs-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <StickyNote className="w-4 h-4" /> Notes
            {allNotes.length > 0 && <Badge variant="secondary" className="text-xs ml-1">{allNotes.length}</Badge>}
          </CardTitle>
          {!adding && (
            <Button variant="outline" size="sm" onClick={() => setAdding(true)} data-testid="add-note-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Note
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {adding && (
          <div className="mb-4 space-y-2 p-3 bg-muted/30 rounded-lg" data-testid="add-note-form">
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Write a customer note..."
              rows={3}
              autoFocus
              data-testid="new-note-input"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => { setAdding(false); setNewNote(''); }} data-testid="cancel-note-btn">
                <X className="w-3.5 h-3.5 mr-1" /> Cancel
              </Button>
              <Button size="sm" onClick={handleSaveNote} disabled={saving || !newNote.trim()} data-testid="save-note-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                Save
              </Button>
            </div>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-8">
            <TabsTrigger value="all" className="text-xs h-7 px-2.5" data-testid="notes-tab-all">
              All {allNotes.length > 0 && `(${allNotes.length})`}
            </TabsTrigger>
            <TabsTrigger value="customer" className="text-xs h-7 px-2.5" data-testid="notes-tab-customer">
              Customer {customerNotes.length > 0 && `(${customerNotes.length})`}
            </TabsTrigger>
            <TabsTrigger value="estimates" className="text-xs h-7 px-2.5" data-testid="notes-tab-estimates">
              Estimates {estimateNotes.length > 0 && `(${estimateNotes.length})`}
            </TabsTrigger>
            <TabsTrigger value="jobs" className="text-xs h-7 px-2.5" data-testid="notes-tab-jobs">
              Jobs {jobNotes.length > 0 && `(${jobNotes.length})`}
            </TabsTrigger>
          </TabsList>

          <div className="mt-3">
            {loading ? (
              <div className="space-y-3 py-2" data-testid="notes-skeleton">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-start gap-3 px-3 py-2.5 animate-pulse">
                    <div className="w-7 h-7 rounded-full bg-muted shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3.5 bg-muted rounded w-3/4" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                      <div className="h-2.5 bg-muted rounded w-1/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : current.length === 0 ? (
              <div className="flex flex-col items-center py-8 text-center" data-testid="notes-empty-state">
                <div className="w-10 h-10 rounded-full bg-muted/60 flex items-center justify-center mb-3">
                  <MessageSquare className="w-5 h-5 text-muted-foreground/50" />
                </div>
                <p className="text-sm font-medium mb-1">No notes yet</p>
                <p className="text-xs text-muted-foreground max-w-[280px] mb-4">
                  Add internal notes to track customer details, job notes, or estimate discussions.
                </p>
                {!adding && (
                  <Button variant="outline" size="sm" onClick={() => setAdding(true)} data-testid="empty-add-note-btn">
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Note
                  </Button>
                )}
              </div>
            ) : (
              <div className="divide-y">
                {current.map(note => <NoteItem key={note.id} note={note} />)}
              </div>
            )}
          </div>
        </Tabs>
      </CardContent>
    </Card>
  );
}
